// =============================================================================
// PERMISSION MATRIX — pure, framework-agnostic core
// =============================================================================
//
// This module is the single source of truth for the Trellis permission matrix.
// It is intentionally dependency-free (no React, no `'use client'`, no Prisma,
// no fetch) so it can be imported from BOTH the client hook
// (`@/hooks/use-permissions`) and the server resolver
// (`@/lib/auth/server-permissions`).
//
// The matrix is the generic resource + action model the product already uses
// (e.g. `workforce`/`approve`, `users`/`create`). Custom roles ("Recursos
// Humanos") store their own `permissions` object in this same shape, which lets
// a member whose enum `role` is MEMBER still hold `users:create` /
// `workforce:approve` when their custom role grants it.

// Workspace member roles (mirrors the `WorkspaceMemberRole` Prisma enum).
export type WorkspaceRole = 'ADMIN' | 'MANAGER' | 'MEMBER' | 'VIEWER'

export const PERMISSION_ACTIONS = [
  'view',
  'create',
  'edit',
  'delete',
  'assign',
  'approve',
  'merge',
] as const

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number]

export function readPermissionActions(value: unknown): PermissionAction[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value.filter((action): action is PermissionAction => PERMISSION_ACTIONS.includes(action))
}

export const PERMISSION_RESOURCE_ACTIONS = {
  agents: ['view', 'create', 'edit', 'delete'],
  calls: ['view'],
  contacts: ['view', 'merge'],
  conversations: ['view', 'create', 'edit'],
  insights: ['view'],
  integrations: ['view', 'create', 'edit'],
  operationsClaims: ['view'],
  operationsCosts: ['view'],
  operationsSupplies: ['view'],
  operationsTags: ['view'],
  operationsTemplates: ['view'],
  operationsVerification: ['view'],
  personalInfo: ['view', 'edit'],
  properties: ['view', 'create', 'edit', 'delete', 'assign'],
  reservations: ['view', 'edit', 'delete'],
  settings: ['view', 'edit'],
  smartDevices: ['view', 'edit'],
  tasks: ['view', 'create', 'edit', 'delete', 'assign', 'approve'],
  users: ['create'],
  workforce: ['view', 'edit', 'delete', 'assign', 'approve'],
} as const satisfies Record<string, readonly PermissionAction[]>

export type PermissionResource = keyof typeof PERMISSION_RESOURCE_ACTIONS

export const PERMISSION_RESOURCES = Object.keys(PERMISSION_RESOURCE_ACTIONS) as PermissionResource[]

export function getSupportedPermissionActions(
  resource: PermissionResource
): readonly PermissionAction[] {
  return PERMISSION_RESOURCE_ACTIONS[resource]
}

export function isPermissionResource(value: string): value is PermissionResource {
  return PERMISSION_RESOURCES.some((resource) => resource === value)
}

// =============================================================================
// DEFAULT ROLE PERMISSIONS
// =============================================================================

// Default permissions per built-in role. Tier 3 of the resolution chain.
export const ROLE_PERMISSIONS: Record<
  WorkspaceRole,
  Record<PermissionResource, PermissionAction[]>
> = {
  ADMIN: {
    agents: ['view', 'create', 'edit', 'delete'],
    calls: ['view'],
    contacts: ['view', 'merge'],
    conversations: ['view', 'create', 'edit'],
    insights: ['view'],
    integrations: ['view', 'create', 'edit'],
    operationsClaims: ['view'],
    operationsCosts: ['view'],
    operationsSupplies: ['view'],
    operationsTags: ['view'],
    operationsTemplates: ['view'],
    operationsVerification: ['view'],
    personalInfo: ['view', 'edit'],
    properties: ['view', 'create', 'edit', 'delete', 'assign'],
    reservations: ['view', 'edit', 'delete'],
    settings: ['view', 'edit'],
    smartDevices: ['view', 'edit'],
    tasks: ['view', 'create', 'edit', 'delete', 'assign', 'approve'],
    users: ['create'],
    workforce: ['view', 'edit', 'delete', 'approve'],
  },
  MANAGER: {
    agents: ['view', 'edit'],
    calls: ['view'],
    contacts: ['view', 'merge'],
    conversations: ['view', 'create', 'edit'],
    insights: [],
    integrations: ['view'],
    operationsClaims: ['view'],
    operationsCosts: ['view'],
    operationsSupplies: ['view'],
    operationsTags: ['view'],
    operationsTemplates: ['view'],
    operationsVerification: ['view'],
    personalInfo: ['view', 'edit'],
    properties: ['view', 'create', 'edit', 'assign'],
    reservations: ['view', 'edit', 'delete'],
    settings: ['view'],
    smartDevices: ['view', 'edit'],
    // `approve` sits here for the same reason `workforce.approve` does: a
    // manager runs the day. Without it a task that reaches REQUIRES_APPROVAL,
    // or carries an owner-approval reason, can never be moved out of that
    // state by the person supervising the work, so a finished clean cannot be
    // closed and the unit never reads as ready. The role could already delete
    // the task outright, which makes withholding approval the stranger half of
    // the pair.
    tasks: ['view', 'create', 'edit', 'delete', 'assign', 'approve'],
    users: [],
    workforce: ['view', 'edit', 'delete', 'approve'],
  },
  MEMBER: {
    agents: ['view'],
    calls: ['view'],
    contacts: ['view'],
    conversations: ['view', 'create', 'edit'],
    insights: [],
    integrations: [],
    operationsClaims: ['view'],
    operationsCosts: [],
    operationsSupplies: ['view'],
    operationsTags: [],
    operationsTemplates: [],
    operationsVerification: [],
    personalInfo: [],
    properties: ['view'],
    reservations: ['view'],
    settings: [],
    smartDevices: ['view'],
    tasks: ['view', 'create', 'edit'],
    users: [],
    workforce: ['view'],
  },
  VIEWER: {
    agents: [],
    calls: ['view'],
    contacts: ['view'],
    conversations: ['view'],
    insights: [],
    integrations: [],
    operationsClaims: ['view'],
    operationsCosts: [],
    operationsSupplies: ['view'],
    operationsTags: [],
    operationsTemplates: [],
    operationsVerification: [],
    personalInfo: [],
    properties: ['view'],
    reservations: ['view'],
    settings: [],
    smartDevices: ['view'],
    tasks: ['view'],
    users: [],
    workforce: ['view'],
  },
}

// =============================================================================
// PERMISSION RESOLUTION
// =============================================================================

/**
 * Plain input shape for {@link resolvePermissions}. Callers adapt their own
 * member/settings representation into this so the resolver stays free of any
 * client (`WorkspaceMemberData`) or server (Prisma row) dependency.
 */
interface ResolvePermissionsInput {
  /**
   * The custom role's permissions matrix, when the member has an active custom
   * role. `null`/`undefined` means no custom role assigned.
   */
  customRolePermissions?: Record<string, string[]> | null
  /** The member's built-in role enum value. */
  role: WorkspaceRole
  /**
   * The workspace-level override for built-in roles, read from
   * `workspace.settings.rolePermissions[role]`. `null`/`undefined` means no
   * override configured for this role.
   */
  workspaceRolePermissionsOverride?: Record<string, string[]> | null
}

/**
 * Resolve a member's effective permission matrix using the three-tier chain:
 *   1. Custom role's `permissions` (if assigned) — takes priority over the enum
 *      role, so a MEMBER with a custom role keeps that role's grants.
 *   2. Workspace-level override for the built-in role
 *      (`settings.rolePermissions[role]`).
 *   3. Hardcoded {@link ROLE_PERMISSIONS} defaults.
 */
export function resolvePermissions(input: ResolvePermissionsInput): Record<string, string[]> {
  let resolved: Record<string, string[]>
  // 1. Custom role permissions take priority.
  if (input.customRolePermissions) {
    resolved = {
      calls: ['view'],
      ...input.customRolePermissions,
    }
  } else if (input.workspaceRolePermissionsOverride) {
    // 2. Workspace-level override for the built-in role.
    resolved = {
      ...(ROLE_PERMISSIONS[input.role] ?? {}),
      ...input.workspaceRolePermissionsOverride,
    }
  } else {
    // 3. Hardcoded defaults.
    resolved = ROLE_PERMISSIONS[input.role] ?? {}
  }

  // Saved roles can predate settings:view. Editors still need to open settings.
  if (resolved.settings?.includes('edit') && !resolved.settings.includes('view')) {
    return { ...resolved, settings: ['view', ...resolved.settings] }
  }
  return resolved
}

/**
 * Check whether a resolved permission matrix grants `action` on `resource`.
 */
export function matrixHasPermission(
  resolved: Record<string, unknown>,
  resource: PermissionResource,
  action: PermissionAction
): boolean {
  if (!getSupportedPermissionActions(resource).includes(action)) {
    return false
  }
  const resourcePermissions = resolved[resource]
  if (!Array.isArray(resourcePermissions)) {
    return false
  }
  return resourcePermissions.includes(action)
}
